export const PrivacyContent = () => (
    <>
        <h3>CensusZen Privacy Policy</h3>
        <p>Last updated: September 2026</p>

        <h4>1. Introduction</h4>
        <p>
            CensusZen ("we", "our", "the Service") is operated by CensusZen. This Privacy
            Policy covers all data collected through app.censuszen.com. The Service is
            intended for healthcare professionals in the United States. We acknowledge that
            data processed through the Service may include Protected Health Information (PHI)
            subject to the Health Insurance Portability and Accountability Act (HIPAA).
        </p>

        <h4>2. Information We Collect</h4>
        <p>
            <strong>Account Information:</strong> name, email address, password (hashed by
            Firebase Authentication), and organization (optional).
        </p>
        <p>
            <strong>Medical Documents:</strong> PDF and DOCX files you upload for
            processing, which may contain PHI.
        </p>
        <p>
            <strong>Extracted Clinical Data:</strong> structured fields, HPI narratives, and
            diagnoses generated by AI from your uploaded documents.
        </p>
        <p>
            <strong>Usage Data:</strong> processing metrics, token counts, and feature usage.
            No patient data is included in usage metrics.
        </p>
        <p>
            <strong>Technical Data:</strong> IP address, browser type, and error reports (via
            Sentry, configured to exclude PHI).
        </p>

        <h4>3. How We Use Your Information</h4>
        <p>
            We use the information we collect to: process uploaded documents and generate
            clinical assessments; authenticate your identity and manage your account; monitor
            service health and debug errors; and improve the accuracy and performance of the
            Service.
        </p>
        <p>
            We do <strong>not</strong> use your data to train AI models. We do{' '}
            <strong>not</strong> sell, rent, or share your data for advertising or marketing
            purposes.
        </p>

        <h4>4. AI Processing &amp; Third-Party Services</h4>
        <p>
            <strong>Google Gemini (Vertex AI):</strong> Documents are sent to Google's Gemini
            API for AI extraction and content generation. Google's Vertex AI is covered under
            the Google Cloud Business Associate Agreement (BAA) and does not use customer data
            to train models.
        </p>
        <p>
            <strong>Google Cloud Platform:</strong> Firestore (database), Cloud Storage (file
            storage), and Cloud Run (compute). All covered under the Google Cloud BAA.
        </p>
        <p>
            <strong>Firebase Authentication:</strong> Account management and login. Covered
            under the Google Cloud BAA.
        </p>
        <p>
            <strong>Sentry:</strong> Error monitoring. Receives technical error data
            only &mdash; configured to exclude PHI.
        </p>
        <p>
            <strong>Cloudflare:</strong> DNS hosting for censuszen.com. Application traffic
            does not pass through Cloudflare.
        </p>
        <p>
            <strong>Resend:</strong> Transactional email (invites, verification, password
            resets). Receives the user's email address and name. No patient data.
        </p>
        <p>
            <strong>Stripe:</strong> Billing. Receives the agency billing contact and plan. No
            patient data.
        </p>

        <h4>5. Data Storage &amp; Security</h4>
        <p>
            All data is stored on Google Cloud Platform in US regions. Data is encrypted in
            transit (TLS/HTTPS) and at rest (Google-managed encryption keys). Uploaded files
            are stored in private Cloud Storage buckets and accessed only via time-limited
            signed URLs. Firestore security rules restrict data to authenticated document
            owners. The backend verifies Firebase ID tokens on every request. No PHI is stored
            in application logs.
        </p>

        <h4>6. Data Retention &amp; Deletion</h4>
        <p>
            Medical documents and case data are retained as long as your account is active.
            You may delete individual cases at any time, which removes both Firestore data and
            Cloud Storage files. Account deletion is available upon request and removes all
            associated data. Automated backups (if enabled) follow the same retention and
            access policies. Error monitoring data (Sentry) is retained per Sentry's standard
            retention period (30 days).
        </p>

        <h4>7. HIPAA Compliance</h4>
        <p>
            CensusZen processes data that may constitute PHI under HIPAA. A Google Cloud BAA
            is in place covering Firestore, Cloud Storage, Cloud Run, and Vertex AI.
        </p>
        <p>
            We maintain administrative safeguards (role-based access, audit trails, minimum
            necessary access), technical safeguards (encryption, access controls, secure
            authentication), and physical safeguards (provided by Google Cloud's SOC 2 / ISO
            27001 certified infrastructure). BAAs with additional subprocessors will be
            executed as required.
        </p>
        <p>
            A business associate agreement is available to every customer. Contact{' '}
            <a href="mailto:privacy@censuszen.com">privacy@censuszen.com</a> to request one.
        </p>

        <h4>8. Your Rights</h4>
        <p>
            <strong>Access:</strong> View all data associated with your account within the
            application. <strong>Correction:</strong> Edit extracted data and generated content
            directly in the app. <strong>Deletion:</strong> Delete individual cases or request
            full account deletion. <strong>Portability:</strong> Copy generated assessments
            from the application.
        </p>
        <p>
            To exercise any right not available through the app, contact us at the address
            below.
        </p>

        <h4>9. Changes to This Policy</h4>
        <p>
            We may update this policy to reflect changes in our practices or legal
            requirements. Material changes will be communicated via email or in-app
            notification. Continued use of the Service after changes constitutes acceptance.
            The "Last updated" date at the top reflects the most recent revision.
        </p>

        <h4>10. Contact Us</h4>
        <p>
            Email:{' '}
            <a href="mailto:privacy@censuszen.com">privacy@censuszen.com</a>
        </p>
        <p>
            For privacy concerns or data requests, please include "Privacy" in the subject
            line.
        </p>
    </>
);
